import { BasicFilterSelectField, InputField } from 'lib/iterable-hook-form';
import { FIELD_NAMES } from 'modules/projectSettings/constants';
import { useProjectSettingsQueryContext } from 'modules/projectSettings/contexts';
import { getProjectIdentifier } from 'modules/projectSettings/utils';
import { useMemo } from 'react';
import styled from 'styled-components';
import { FormField } from 'ui/formField';
import { Identifier } from '../ProjectSettingsPage.styled';
import { ConnectedFormField, MarkdownDescription, OptionalLabelText } from './ConnectedFormField';
import { FormSection } from './FormSection';
import { LogoImageField } from './LogoImageField';

const { NAME, LOGO_URL, TIME_ZONE, PHYSICAL_ADDRESS } = FIELD_NAMES;

const StyledSelect = styled(BasicFilterSelectField)`
  width: 100%;
`;

export const PROJECT_SECTION_TEXT = {
  /** Generic strings */
  descriptionSuffix: '-description',
  fieldIdSuffix: '-field',
  formFieldIdSuffix: '-form-field',
  /** Logo URL */
  logoUrlDescriptionText:
    'Display your logo on the project’s default subscription pages. Recommend 300px wide.',
  logoUrlLabelText: 'Logo',
  /** Name */
  nameDescriptionText: 'Name of your project. Also used for the `{{companyName}}` merge parameter.',
  nameLabelText: 'Name',
  /** Physical address */
  physicalAddressDescriptionText:
    'This address is accessible through the `{{physicalAddress}}` merge parameter.',
  physicalAddressDescriptionTextId: 'physicalAddressDescriptionText',
  physicalAddressLabelText: 'Physical mailing address',
  /** Project section */
  sectionHeadingText: 'Project',
  sectionId: 'project',
  /** Timezone */
  timeZoneDescriptionText: 'The default time zone for scheduling campaigns.',
  timeZoneLabelText: 'Time zone',
  /** Unique identifier */
  identifierLabelText: 'Unique identifier',
};

export const ProjectFormSection = () => {
  const { timeZones } = useProjectSettingsQueryContext();
  const { project } = useProjectSettingsQueryContext();
  const { isUuidBased, uuidProjectType } = project;

  const identifier = getProjectIdentifier(isUuidBased, uuidProjectType);
  const timezoneOptions = useMemo(
    () =>
      timeZones.map((tz) => ({
        id: tz,
        label: tz,
        value: tz,
      })),
    [timeZones],
  );

  return (
    <FormSection
      id={PROJECT_SECTION_TEXT.sectionId}
      data-test={`${PROJECT_SECTION_TEXT.sectionId}-form-section`}
      headingText={PROJECT_SECTION_TEXT.sectionHeadingText}
      scrollAnchorOffset={-230}
    >
      <ConnectedFormField
        data-test={`${NAME}${PROJECT_SECTION_TEXT.formFieldIdSuffix}`}
        descriptionText={
          <MarkdownDescription
            id={`${NAME}${PROJECT_SECTION_TEXT.descriptionSuffix}`}
            markdownText={PROJECT_SECTION_TEXT.nameDescriptionText}
          />
        }
        labelText={PROJECT_SECTION_TEXT.nameLabelText}
        name={NAME}
      >
        <InputField name={NAME} id={NAME} />
      </ConnectedFormField>

      <FormField
        data-test={`uniqueIdentifier${PROJECT_SECTION_TEXT.formFieldIdSuffix}`}
        labelText={PROJECT_SECTION_TEXT.identifierLabelText}
      >
        <Identifier data-test={`uniqueIdentifier${PROJECT_SECTION_TEXT.fieldIdSuffix}`}>
          {identifier}
        </Identifier>
      </FormField>

      <ConnectedFormField
        data-test={`${LOGO_URL}${PROJECT_SECTION_TEXT.formFieldIdSuffix}`}
        descriptionText={PROJECT_SECTION_TEXT.logoUrlDescriptionText}
        labelText={<OptionalLabelText text={PROJECT_SECTION_TEXT.logoUrlLabelText} />}
        name={LOGO_URL}
      >
        <LogoImageField />
      </ConnectedFormField>

      <ConnectedFormField
        data-test={`${TIME_ZONE}${PROJECT_SECTION_TEXT.formFieldIdSuffix}`}
        descriptionText={PROJECT_SECTION_TEXT.timeZoneDescriptionText}
        labelText={PROJECT_SECTION_TEXT.timeZoneLabelText}
        name={TIME_ZONE}
      >
        <StyledSelect
          automaticallyAvoidCollisions
          name={TIME_ZONE}
          id={TIME_ZONE}
          options={timezoneOptions}
        />
      </ConnectedFormField>

      <ConnectedFormField
        data-test={`${PHYSICAL_ADDRESS}${PROJECT_SECTION_TEXT.formFieldIdSuffix}`}
        name={PHYSICAL_ADDRESS}
        labelText={<OptionalLabelText text={PROJECT_SECTION_TEXT.physicalAddressLabelText} />}
        descriptionText={
          <MarkdownDescription
            id={PROJECT_SECTION_TEXT.physicalAddressDescriptionTextId}
            markdownText={PROJECT_SECTION_TEXT.physicalAddressDescriptionText}
          />
        }
      >
        <InputField name={PHYSICAL_ADDRESS} id={PHYSICAL_ADDRESS} />
      </ConnectedFormField>
    </FormSection>
  );
};
